import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import React from "react";
import { useNavigate } from "react-router-dom";

const steps = [
    { label: "Draft", state: "DRAFT" },
    { label: "Ready to review", state: "READY_TO_REVIEW" }, 
    { label: "Reviewed", state: "REVIEWED" },
    { label: "Published", state: "PUBLISHED" },

];

// Custom step icon component that shows numbers instead of check marks
function CustomStepIcon({ icon, isCurrentStep }) {
    return (
        <Box
            sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: isCurrentStep ? 'primary.main' : 'grey.300',
                color: isCurrentStep ? 'white' : 'grey.600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 'bold'
            }}
        >
            {icon}
        </Box>
    );
}

export default function PuzzleWorkflowStepper({ currentState, puzzleId, size = "normal", onStepClick, align = "center", puzzleType }) {
    const navigate = useNavigate();

    // Determine base path based on puzzle type
    const getBasePath = () => {
        if (puzzleType === 'CROSSWORD') {
            return '/admin/puzzle/cross_board';
        }
        if (puzzleType === 'WORD_WHEEL') {
            return '/admin/puzzle/word_wheel_board';
        }
        return '/admin/puzzle/grid_board';
    };

    const getCurrentStepIndex = () => {
        return steps.findIndex(step => step.state === currentState);
    };

    const handleStepClick = (index) => {
        if (puzzleId) {
            // Navigate back to board main with the selected state filter
            navigate(`${getBasePath()}?activeStep=${index}`);
        } else {
            // For board main, we need to trigger a callback instead of navigation
            if (onStepClick) {
                onStepClick(index);
            }
        }
    };

    const isSmall = size === "small";
    const currentStepIndex = getCurrentStepIndex();

    return (
        <Paper 
            elevation={isSmall ? 1 : 2} 
            sx={{ 
                p: isSmall ? 1.5 : 2, 
                mb: isSmall ? 1.5 : 2,
                width: "100%"
            }}
        >
            <Stepper 
                activeStep={currentStepIndex >= 0 ? currentStepIndex : steps.length - 1} 
                alternativeLabel={align === "center"}
                orientation="horizontal"
                sx={{
                    width: "100%",
                    "& .MuiStepper-root": {
                        padding: isSmall ? "8px 0" : "12px 0"
                    },
                    "& .MuiStepConnector-root": {
                        flex: "1 1 auto",
                        minWidth: 0
                    },
                    "& .MuiStep-root": {
                        flex: "0 0 auto",
                        paddingLeft: isSmall ? "8px" : "12px",
                        paddingRight: isSmall ? "8px" : "12px"
                    },
                    "& .MuiStepLabel-root": {
                        flexDirection: align === "center" ? "column" : "row",
                        alignItems: "center"
                    },
                    justifyContent: align === "left" ? "flex-start" : "space-between"
                }}
            >
                {steps.map((step, index) => (
                    <Step key={step.label} completed={false}>
                        <StepLabel 
                            slots={{
                                stepIcon: (props) => (
                                    <CustomStepIcon
                                        icon={index + 1}
                                        completed={props.completed}
                                        active={props.active}
                                        isCurrentStep={index === currentStepIndex}
                                    />
                                )
                            }}
                            sx={{ 
                                cursor: "pointer",
                                margin: `0 ${isSmall ? "4px" : "8px"}`,
                                "& .MuiStepLabel-label": {
                                    "&:hover": {
                                        color: "primary.main"
                                    },
                                    fontWeight: index === currentStepIndex ? "bold" : "normal",
                                    color: index === currentStepIndex ? "primary.main" : "inherit",
                                    fontSize: isSmall ? "0.75rem" : "0.875rem"
                                }
                            }}
                            onClick={() => handleStepClick(index)}
                        >
                            {step.label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </Paper>
    );
}

